import apisauce, { ApisauceInstance } from 'apisauce';

import config from '../../config';
import { ExtraUserDetails } from '../models/auth/types';
import { AcePortalClientTypes } from '../util/auth/constants';
import { NullableString } from '../util/types';

import { ICommonAuthService } from './types';

/**
 * Path params marked given as $1, $2 etc.
 */
const API_PATH = {
  signupToken: '/api/v1/User/sign-up-token',
  /**
   * Has 3 functions:
   *  - creates a user (new record in Users table)
   *  - gets first name, last name and country of residence as input (to create a new record in Individuals table)
   *  - creates the user for both 10n Web App and Mobile App, no matter which was used to register
   * This endpoint has the following implementations:
   *  1. /api/v1/User - the original one
   *  2. /api/v1/User/with-sending-details - was added to enable country of residence (see CRM.service.ts `/country/list`)
   *  3. /api/v1/User/bulk-insert-clients - was added to extend 2. - when used it also adds all 10n clients to the account,
   *     so that a 10n account registered in 10n Mobile App would also be available for 10n Web App.
   * Only the last number 3. is to be used with 10n apps as it has all needed in one package.
   * */
  registerUserWithAllClients: '/api/v1/User/bulk-insert-clients',
  confirmEmail: '/api/v1/User/confirm-email',
  resendEmail: '/api/v1/User/send-confirmation-email',
  /**
   * @deprecated Integrated before but there is no usage in WEB and MOBILE
   */
  userPermissions: '/api/v1/User/permissions',
  phoneNumberSendCode: '/api/v1/User/phone-number/send-code',
  phoneNumberVerify: '/api/v1.1/User/phone-number/verify',
  /**
   * @deprecated Integrated before but there is no usage in WEB and MOBILE
   */
  putAssignedClients: '/api/v1/User/$1/assigned-clients',
  sendResetPassword: '/api/v1/User/send-reset-password',
  sendNonAppropriatenessEmail: '/api/v1/User/send-non-appropriateness/$1',
};

const appendQueryParam = (url: string, paramName: string, paramValue: NullableString) => {
  if (!paramValue) return url;

  const [ baseUrl, hash ] = url.split('#');
  const separator = baseUrl.includes('?') ? '&' : '?';
  const hashSuffix = hash ? `#${hash}` : '';

  return `${baseUrl}${separator}${encodeURIComponent(paramName)}=${encodeURIComponent(paramValue)}${hashSuffix}`;
};

export default class CommonAuthService implements ICommonAuthService {
  private api: ApisauceInstance;
  private bffApi: ApisauceInstance;
  private phone: string | null = null;

  constructor() {
    this.api = apisauce.create({ baseURL: `${config.auth.issuer}`, withCredentials: true });
    this.bffApi = apisauce.create({
      baseURL: `https://${config.gateway.url}/auth`,
      withCredentials: true,
      headers: { 'X-CSRF': '1' },
    });
  }

  registerUser(
    email: string,
    password: string,
    confirmPassword: string,
    userExtraDetails = ((null as any) as ExtraUserDetails),
    selectedTradingPlanId: NullableString = null,
    autoPass: boolean = false,
    referrerClientId: NullableString = null,
    referralCode: NullableString = null,
  ) {
    const request = {
      email,
      password,
      confirm_password: confirmPassword,
      tenant_id: config.auth.tenantId,
      client: referrerClientId ?? config.auth.clientId,
      redirect_url: appendQueryParam(config.auth.redirectUrlConfirmEmail, 'clientId', referrerClientId),
      send_email: true,
      clients: AcePortalClientTypes,
      user_type_relation: 1,
      ...(userExtraDetails ?? {}),
    };

    if (selectedTradingPlanId) {
      request.trading_plan_id = selectedTradingPlanId;
    }

    if (autoPass) {
      request.auto_pass = autoPass;
    }

    if (referralCode) {
      request.referral = referralCode;
    }

    return this.bffApi.post<any>(
      API_PATH.registerUserWithAllClients,
      request,
    );
  }

  confirmEmail(email: string, token?: NullableString, referrerClientId?: NullableString) {
    return this.bffApi.post<any>(
      API_PATH.confirmEmail,
      {
        email,
        token,
        client: referrerClientId ?? config.auth.clientId,
        redirect_url: config.auth.redirectUrlEmailConfirmed,
      },
    );
  }

  resendEmail(
    email: string,
    first_name: NullableString,
    last_name: NullableString,
    country_of_residence: NullableString,
    selectedTradingPlanId?: NullableString,
    autoPass?: boolean,
    referrerClientId?: NullableString,
    referralCode?: NullableString,
  ) {
    const request = {
      email,
      client: referrerClientId ?? config.auth.clientId,
      return_url: appendQueryParam(config.auth.redirectUrlConfirmEmail, 'clientId', referrerClientId),
      show_current_password: true,
      first_name,
      last_name,
      country_of_residence,
      trading_plan_id: selectedTradingPlanId,
      auto_pass: autoPass,
      referral: referralCode,
    };

    if (!selectedTradingPlanId) {
      delete request.trading_plan_id;
    }

    if (!autoPass) {
      delete request.auto_pass;
    }

    if (!referralCode) {
      delete request.referral;
    }

    return this.bffApi.post<any>(
      API_PATH.resendEmail,
      request,
    );
  }

  sendPhoneCode(userId: number, phone: string) {
    this.phone = phone;
    return this.bffApi.post<any>(
      API_PATH.phoneNumberSendCode,
      {
        id: userId,
        phone_number: phone,
      },
    );
  }

  verifyPhoneNumber(userId: number, code: string) {
    return this.bffApi.post<any>(
      API_PATH.phoneNumberVerify,
      {
        id: userId,
        phone_number: this.phone,
        code,
        redirect_url: config.auth.redirectUrlPhoneVerified,
        show_current_password: false,
      },
    );
  }



  sendResetPassword(email: string, client: string, returnUrl: string) {
    return this.bffApi.post(API_PATH.sendResetPassword, {
      email,
      client,
      return_url: returnUrl,
      show_current_password: true,
    });
  }

  sendNonAppropriatenessEmail(email: string) {
    return this.bffApi.get(API_PATH.sendNonAppropriatenessEmail.replace('$1', email));
  }


  public async redeemSignupToken(username: string, signupToken: string, returnUrl?: string): Promise<void> {
    const response = await fetch(config.auth.bffRedeemSignupUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF': '1' },
      body: JSON.stringify({ username, signupToken, ...(returnUrl ? { returnUrl } : {}) }),
    });

    if (response.status === 204 || response.status === 200) return;

    const error = new Error(response.status === 401 ? 'SIGNUP_TOKEN_INVALID' : 'REDEEM_FAILED');
    (error as any).status = response.status;
    throw error;
  }
}
